import { useCallback, useEffect, useMemo, useState } from "react";

import { DEFAULT_DEBOUNCE_TIME } from "consts";
import { debounce } from "helpers/debounce";

export default function usePersistedState<T extends boolean | string | null | undefined>({
  key,
  defaultValue,
  deferWrite = false
}: {
  key: string;
  defaultValue: T;
  deferWrite?: boolean;
}): [T, (newValue: T) => void] {
  const [value, setValue] = useState<string | T>(() => {
    try {
      return localStorage.getItem(key) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const persist = useCallback(
    (newValue: T) => {
      try {
        localStorage.setItem(key, String(newValue));
      } catch {
        // storage unavailable; keep in-memory state only
      }
    },
    [key]
  );

  const debouncedPersist = useMemo(
    () => debounce(persist, DEFAULT_DEBOUNCE_TIME),
    [persist]
  );

  useEffect(() => () => debouncedPersist.flush(), [debouncedPersist]);

  const setAndPersistValue = useCallback(
    (newValue: T) => {
      if (deferWrite) {
        debouncedPersist(newValue);
      } else {
        persist(newValue);
      }
      setValue(newValue);
    },
    [deferWrite, debouncedPersist, persist]
  );

  const coerce = (raw: string | T): T => {
    if (raw === "undefined") {
      return undefined as T;
    }
    if (raw === "null") {
      return null as T;
    }
    if (raw === "true" || raw === "false") {
      return (raw === "true") as T;
    }
    return raw as T;
  };

  return [coerce(value), setAndPersistValue];
}
